/**
 * Single Story — scroll choreography
 * -----------------------------------
 * Requires GSAP + ScrollTrigger (already loaded on this site).
 *
 * Desktop:
 *   Act 1 (1vh scroll): hero image shrinks from 80vw (right-aligned) to the
 *     narrow center column; hero title + excerpt overlay fade out; 3-col
 *     grid fades in.
 *   Act 2+3 (pinned): bio column scrolls up first, then content column
 *     scrolls when bio is ~halfway up. Image stays pinned in the center.
 *
 * Mobile:
 *   Flow layout. A fixed-position compact bar (thumbnail + bio name) fades
 *   in when the bio card scrolls past the navbar.
 *
 * Also binds the scroll-hint button to smooth-scroll to the 3-col grid.
 */
(function () {
    'use strict';

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init() {
        var page = document.querySelector('[data-scroll-story]');
        if (!page) return;

        var DESKTOP_MQ = window.matchMedia('(min-width: 992px)');
        var REDUCED    = window.matchMedia('(prefers-reduced-motion: reduce)');

        // Scroll-hint button — works in both modes
        bindScrollHint(page);

        if (DESKTOP_MQ.matches && !REDUCED.matches) {
            initDesktop(page);
        } else {
            initMobile(page);
        }

        DESKTOP_MQ.addEventListener('change', function () {
            teardown(page);
            if (DESKTOP_MQ.matches && !REDUCED.matches) initDesktop(page);
            else initMobile(page);
        });
    }

    /* ---------- shared helpers ---------- */

    function bindScrollHint(page) {
        var btn = page.querySelector('[data-story-scroll-hint]');
        if (!btn) return;
        btn.addEventListener('click', function (e) {
            e.preventDefault();
            var target = page.querySelector('[data-story-columns]');
            if (!target) return;
            var navOffset = getNavOffset();
            var targetY = window.scrollY + target.getBoundingClientRect().top - navOffset - 20;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
        });
    }

    function getNavOffset() {
        var v = getComputedStyle(document.documentElement)
                .getPropertyValue('--site-nav-height').trim();
        return parseFloat(v) || 70;
    }

    function teardown(page) {
        if (window.ScrollTrigger) {
            window.ScrollTrigger.getAll().forEach(function (st) {
                if (st.vars && st.vars.id && st.vars.id.indexOf('story-') === 0) st.kill();
            });
        }
        // Reset inline styles GSAP set
        ['[data-story-stage]', '[data-story-image-frame]', '[data-story-image-overlay]', '[data-story-hero]',
         '[data-story-columns]', '[data-story-content]', '[data-story-bio]'].forEach(function (sel) {
            var el = page.querySelector(sel);
            if (el) el.removeAttribute('style');
        });
        var sticky = page.querySelector('[data-story-bio-sticky]');
        if (sticky) sticky.classList.remove('is-visible');
    }


    /* =====================================================================
       DESKTOP
    ===================================================================== */

    function initDesktop(page) {
        if (!window.gsap || !window.ScrollTrigger) {
            console.warn('[story] GSAP + ScrollTrigger required for desktop animation.');
            return;
        }
        window.gsap.registerPlugin(window.ScrollTrigger);

        var stage    = page.querySelector('[data-story-stage]');
        var frame    = page.querySelector('[data-story-image-frame]');
        var overlay  = page.querySelector('[data-story-image-overlay]');
        var heroText = page.querySelector('[data-story-hero]');
        var columns  = page.querySelector('[data-story-columns]');
        var content  = page.querySelector('[data-story-content]');
        var bio      = page.querySelector('[data-story-bio]');
        var slot     = page.querySelector('.story-image-slot');
        if (!stage || !frame || !heroText || !columns || !content || !bio || !slot) return;

        // Set --main-nav-height + --site-nav-height from the actual elements
        // so .story-vh resolves correctly even if mega-menu.js hasn't run yet
        // (or isn't on this page).
        function syncNavHeight() {
            var navEl     = document.querySelector('.site-nav');
            var utilityEl = document.querySelector('.navigation-utility');
            var navH      = navEl ? navEl.offsetHeight : 0;
            var utilH     = (utilityEl && utilityEl.offsetParent !== null)
                          ? utilityEl.offsetHeight : 0;
            if (navH) {
                document.documentElement.style.setProperty('--main-nav-height', navH + 'px');
                document.documentElement.style.setProperty('--site-nav-height', (navH + utilH) + 'px');
            }
        }
        syncNavHeight();

        // Defer building the scroll setup until after fonts and the hero
        // image have loaded — these change layout and would invalidate any
        // measurements taken before they settle.
        var heroImg = frame.querySelector('img');
        var ready = Promise.resolve();
        if (document.fonts && document.fonts.ready) ready = ready.then(function () { return document.fonts.ready; });
        if (heroImg && !heroImg.complete) {
            ready = ready.then(function () {
                return new Promise(function (resolve) {
                    heroImg.addEventListener('load',  resolve, { once: true });
                    heroImg.addEventListener('error', resolve, { once: true });
                });
            });
        }

        ready.then(function () {
            syncNavHeight();
            buildTimelines();
        });

        function buildTimelines() {
            // Measure slot in its real laid-out position. The slot has
            // height:0 so it doesn't bloat the grid row, so we use the slot
            // only for x-position; height comes from --story-vh.
            columns.style.visibility = 'hidden';
            void columns.offsetWidth;
            var slotRect  = slot.getBoundingClientRect();
            var stageRect = stage.getBoundingClientRect();
            // Measure the frame in its untouched hero state so the act-1 tween
            // can reverse back to it exactly. The hero height is set in CSS
            // (--story-hero-h, shorter than the stage so the overlay clears the
            // fold); a one-ended .to() would let GSAP capture an unreliable
            // start and snap the frame back to full height when scrolling up.
            var frameRect = frame.getBoundingClientRect();
            columns.style.visibility = '';

            var heroLeft   = frameRect.left - stageRect.left;
            var heroTop    = frameRect.top  - stageRect.top;
            var heroWidth  = frameRect.width;
            var heroHeight = frameRect.height;

            var targetLeft  = slotRect.left - stageRect.left;
            var targetWidth = slotRect.width;
            // Image's vertical placement matches its starting state: anchored
            // top:gutter-y, height:--story-vh. Read the live computed value
            // of --story-vh so it tracks viewport changes.
            // Read resolved values from computed styles (in px). CSS custom
            // properties aren't resolved to px by getComputedStyle, so we read
            // them off elements where they're applied as actual properties.
            var storyVh = parseFloat(getComputedStyle(columns).height) || window.innerHeight;
            var insetY  = parseFloat(getComputedStyle(columns).paddingTop) || 0;
            var targetTop    = insetY;
            var targetHeight = storyVh - (insetY * 2);

            // ACT 1: shrink hero + slide content/bio up from below
            window.gsap.timeline({
                scrollTrigger: {
                    id: 'story-act1',
                    trigger: stage,
                    start: 'top top',
                    end: '+=100%',
                    scrub: 0.6
                }
            })
            .fromTo(frame, {
                left: heroLeft + 'px',
                right: 'auto',
                width: heroWidth + 'px',
                top: heroTop + 'px',
                bottom: 'auto',
                height: heroHeight + 'px'
            }, {
                left: targetLeft + 'px',
                right: 'auto',
                width: targetWidth + 'px',
                top: targetTop + 'px',
                bottom: 'auto',
                height: targetHeight + 'px',
                ease: 'none'
            }, 0)
            .to(heroText, { opacity: 0, ease: 'none' }, 0)
            .to(overlay || {}, { opacity: 0, ease: 'none', duration: 0.5 }, 0)
            // Snap visibility on at the start, then slide up. Snap uses a
            // tiny duration so it's effectively instant (no perceptible fade).
            .fromTo(content,
                { autoAlpha: 0 },
                { autoAlpha: 1, ease: 'none', duration: 0.001, immediateRender: false },
                0.3)
            .fromTo(content,
                { y: window.innerHeight },
                { y: 0, ease: 'none', duration: 0.7, immediateRender: false },
                0.3)
            .fromTo(bio,
                { autoAlpha: 0 },
                { autoAlpha: 1, ease: 'none', duration: 0.001, immediateRender: false },
                0.35)
            .fromTo(bio,
                { y: window.innerHeight },
                { y: 0, ease: 'none', duration: 0.7, immediateRender: false },
                0.35)
            // Trigger the generic reveal system (fade-up/mask-in/etc.) for
            // content/bio at the same point GSAP starts sliding each column
            // in. Without this, reveal-classed elements inside .story-content
            // and .story-bio would already show as "revealed" from initial
            // page load (the generic scroll observer's near-top-of-document
            // check fires before this section is ever scrolled into view),
            // so the fade/mask effect would never actually play. revealSection
            // is idempotent, so it's safe to call on every scrub pass.
            .call(function () { if (window.revealSection) window.revealSection(content); }, null, 0.3)
            .call(function () { if (window.revealSection) window.revealSection(bio); }, null, 0.35);

            // ACT 2 + 3: pin the stage, then translate bio up (act 2), then
            // translate content up (act 3). Act 1 (image shrink) occupies the
            // first `innerHeight` worth of scroll; bio/content translations
            // don't start until after act 1 is done.
            function distances() {
                var cs = getComputedStyle(columns);
                var visible = columns.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
                return {
                    bio:     Math.max(0, bio.scrollHeight     - visible),
                    content: Math.max(0, content.scrollHeight - visible)
                };
            }
            var d = distances();
            var act1Length = window.innerHeight;
            function computePinLength() {
                var contentEnd = act1Length + d.content;
                var bioEnd     = act1Length + d.bio;
                return Math.max(contentEnd, bioEnd);
            }

            var pinTL = window.gsap.timeline({
                scrollTrigger: {
                    id: 'story-pin',
                    trigger: stage,
                    start: 'top top',
                    end: function () { return '+=' + computePinLength(); },
                    pin: true,
                    pinSpacing: true,
                    scrub: true,
                    invalidateOnRefresh: true,
                    onRefreshInit: function () {
                        d = distances();
                        act1Length = window.innerHeight;
                    }
                }
            });

            // Bio translation begins only after act 1 is complete.
            if (d.bio > 0) {
                pinTL.fromTo(bio,
                    { y: 0 },
                    { y: -d.bio, ease: 'none', duration: d.bio },
                    act1Length
                );
            }

            // Content translation begins right after act 1.
            if (d.content > 0) {
                pinTL.fromTo(content,
                    { y: 0 },
                    { y: -d.content, ease: 'none', duration: d.content },
                    act1Length
                );
            }

            // Re-measure once everything is built, in case anything shifted
            window.ScrollTrigger.refresh();
        }

        // Resize handler: kill story triggers and rebuild with fresh measurements.
        // Throttled via rAF to coalesce burst events.
        var resizeRAF = null;
        window.addEventListener('resize', function () {
            if (resizeRAF) return;
            resizeRAF = requestAnimationFrame(function () {
                resizeRAF = null;
                if (!window.ScrollTrigger) return;
                window.ScrollTrigger.getAll().forEach(function (st) {
                    if (st.vars && st.vars.id && st.vars.id.indexOf('story-') === 0) st.kill();
                });
                stage.removeAttribute('style');
                frame.removeAttribute('style');
                heroText.removeAttribute('style');
                columns.removeAttribute('style');
                bio.removeAttribute('style');
                content.removeAttribute('style');
                if (overlay) overlay.removeAttribute('style');
                syncNavHeight();
                buildTimelines();
            });
        });
    }


    /* =====================================================================
       MOBILE — sticky compact bar
    ===================================================================== */

    function initMobile(page) {
        var bio      = page.querySelector('[data-story-bio]');
        var sticky   = page.querySelector('[data-story-bio-sticky]');
        if (!bio || !sticky) return;

        // Use the bio card's head (which contains the name) as the
        // handoff point. As the head crosses behind the navbar, the
        // sticky bar fades in — feels like a smooth hand-off rather
        // than waiting for the entire bio card to disappear first.
        var bioHead = bio.querySelector('.story-bio-head') || bio;

        function getMainNavOffset() {
            var v = getComputedStyle(document.documentElement)
                    .getPropertyValue('--main-nav-height').trim();
            return parseFloat(v) || 70;
        }

        function onScroll() {
            var headRect  = bioHead.getBoundingClientRect();
            var navOffset = getMainNavOffset();

            // Show the bar when the bottom of the name-head has scrolled
            // up to just below the navbar. By then the name is gone behind
            // the nav and the sticky bar takes its place.
            var show = headRect.bottom < navOffset + 8;

            sticky.classList.toggle('is-visible', show);
            sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        }

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        onScroll();
    }

})();